import * as React from "react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import { Alert, Box, Button, Card, CardContent, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useAdminCampaignBriefCalendar, useClientCampaignBriefCalendar } from "@/hooks/api/use-campaign-briefs";

import { calendarRange, toCalendarEvent } from "./calendar-events";

// dayGrid and list are free; the timeline views are a premium plugin and are not used.
const plugins = [dayGridPlugin, listPlugin];

/**
 * First dials and redials by date: every account's for AdaptiveX staff (`admin`), or the signed-in
 * client's own (`client`). Only the hook that matches the scope is given a range, so only it loads.
 */
export function BriefsCalendar({ scope }: { scope: "admin" | "client" }): React.JSX.Element {
	const navigate = useNavigate();
	const [range, setRange] = React.useState<{ from: string; to: string } | null>(null);
	const [includeCancelled, setIncludeCancelled] = React.useState(false);

	const admin = useAdminCampaignBriefCalendar(scope === "admin" ? range : null, includeCancelled);
	const client = useClientCampaignBriefCalendar(scope === "client" ? range : null, includeCancelled);
	const calendar = scope === "admin" ? admin : client;
	const events = React.useMemo(
		() => (calendar.data ?? []).map((slot) => toCalendarEvent(slot, { showAccount: scope === "admin" })),
		[calendar.data, scope]
	);

	return (
		<Stack spacing={2}>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between" }}>
				<Typography variant="body2" color="text.secondary" sx={{ alignSelf: { sm: "center" } }}>
					Hours are local to each request&apos;s regions. Filled is the first dial, outlined a redial.
				</Typography>
				<FormControlLabel
					control={
						<Switch checked={includeCancelled} onChange={(event) => setIncludeCancelled(event.target.checked)} />
					}
					label="Show cancelled"
				/>
			</Stack>

			{calendar.isError ? (
				<Alert
					severity="warning"
					action={
						<Button color="inherit" size="small" onClick={() => calendar.refetch()}>
							Retry
						</Button>
					}
				>
					We couldn&apos;t load the calendar right now.
				</Alert>
			) : null}

			<Card>
				<CardContent>
					<Box
						sx={{
							"--fc-border-color": "var(--mui-palette-divider)",
							"--fc-page-bg-color": "transparent",
							"--fc-neutral-bg-color": "var(--mui-palette-background-level1)",
							"--fc-list-event-hover-bg-color": "var(--mui-palette-action-hover)",
							"--fc-today-bg-color": "var(--mui-palette-action-hover)",
							"--fc-button-bg-color": "var(--mui-palette-primary-main)",
							"--fc-button-border-color": "var(--mui-palette-primary-main)",
							"--fc-button-hover-bg-color": "var(--mui-palette-primary-dark)",
							"--fc-button-hover-border-color": "var(--mui-palette-primary-dark)",
							"--fc-button-active-bg-color": "var(--mui-palette-primary-dark)",
							"--fc-button-active-border-color": "var(--mui-palette-primary-dark)",
							"& .fc": { fontFamily: "inherit", color: "var(--mui-palette-text-primary)" },
							// The toolbar does not wrap on its own, so at phone width the view buttons would be clipped.
							"& .fc .fc-toolbar": { flexWrap: "wrap", gap: 1 },
							"& .fc .fc-toolbar-title": { fontSize: "1.1rem", fontWeight: 600 },
							"& .fc .fc-event": { cursor: "pointer" },
							// Wrapped rather than clipped: the name and the clocks come after the time.
							"& .fc .fc-daygrid-event": { whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.35 },
							"& .fc .fc-col-header-cell-cushion, & .fc .fc-daygrid-day-number, & .fc .fc-list-day-cushion a": {
								color: "var(--mui-palette-text-primary)",
								textDecoration: "none",
							},
							// The list view's dot is a filled circle; a ring tells a redial from a first dial there too.
							"& .fc .brief-redial .fc-list-event-dot": { width: 6, height: 6, borderWidth: 2 },
						}}
					>
						<FullCalendar
							plugins={plugins}
							initialView="dayGridMonth"
							headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,listWeek" }}
							buttonText={{ month: "Month", list: "Week list", today: "Today" }}
							firstDay={1}
							height="auto"
							dayMaxEvents={4}
							// Every entry is all-day and starts with its time, so title order is time order.
							eventOrder="title"
							views={{ listWeek: { displayEventTime: false } }}
							noEventsContent="No dials this week"
							events={events}
							datesSet={(info) => setRange(calendarRange(info.startStr, info.endStr))}
							eventDidMount={(info) => {
								info.el.title = info.event.extendedProps.tooltip;
							}}
							eventClick={(info) => {
								info.jsEvent.preventDefault();
								navigate(
									`/${scope === "admin" ? "client-requests" : "campaign-briefs"}/${info.event.extendedProps.briefId}`
								);
							}}
						/>
					</Box>
				</CardContent>
			</Card>
		</Stack>
	);
}
